import { useEffect, useRef, useState } from 'react';
import {
  getValidToken,
  getPlaybackState,
  getCurrentlyPlaying,
  getQueue,
} from '../../Strobe/spotify.js';
import { PlaybackClock, normalizeQueueItem, normalizeSpotifyPlayback } from '../engine/playbackClock';
import { computeBeatState, estimateBpmFromTap } from '../engine/beatSystem';
import { activeVisualEvent } from '../engine/timeline';
import { lyricForDisplay } from '../engine/lyricsApi';
import { enrichTrackLyrics, getCachedTrack, prepareTrack } from '../engine/trackPreparation';
import { makeManualAudioProfile } from '../engine/spotifyAudioProfile';
import { usePartyStore } from '../store/partyStore';
import type { QueueTrackPreview } from '../types';
import { getMicAudioContext } from './useMicPartySync';
import { getSpotifyAudioProfile } from './useSpotifyPartySync';

const POLL_MS = 500;
const MIC_BPM = 128;
const MIN_MIC_BPM = 70;
const MAX_MIC_BPM = 180;
const MIN_BEAT_INTERVAL_MS = 60_000 / MAX_MIC_BPM;
const MAX_BEAT_INTERVAL_MS = 60_000 / MIN_MIC_BPM;
const MIC_VISUAL_UPDATE_MS = 1000 / 30;
const MIC_SIGNAL_STEP = 0.01;

function quantizeMicValue(value: number): number {
  return Math.round(value / MIC_SIGNAL_STEP) * MIC_SIGNAL_STEP;
}

function micIntensityFromEnergy(energy: number, onset: number): number {
  return Math.max(0.25, Math.min(1.25, 0.34 + energy * 1.7 + onset * 2.2));
}

function canonicalBeatInterval(intervalMs: number): number | null {
  if (!Number.isFinite(intervalMs) || intervalMs <= 0) return null;
  let interval = intervalMs;
  if (interval > 720) interval /= 2;
  if (interval < 320) interval *= 2;
  if (interval < MIN_BEAT_INTERVAL_MS || interval > MAX_BEAT_INTERVAL_MS) return null;
  return interval;
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function micErrorMessage(err: unknown): string {
  if (!(err instanceof Error)) return 'Could not start microphone input.';
  if (err.name === 'NotAllowedError' || err.name === 'SecurityError') {
    return 'Microphone permission was blocked. Allow microphone access for this site, then try Spotify + Mic again.';
  }
  if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
    return 'No microphone was found. Connect an input device, then try Spotify + Mic again.';
  }
  if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
    return 'The microphone is already in use by another app or browser tab.';
  }
  return err.message || 'Could not start microphone input.';
}

export function useSpotifyMicPartySync(clientId: string, enabled: boolean) {
  const [error, setError] = useState('');
  const clockRef = useRef(new PlaybackClock());
  const rafRef = useRef<number | undefined>(undefined);
  const lastTrackIdRef = useRef<string | null>(null);
  const lastProfileKeyRef = useRef<string | null>(null);
  const beatIndexRef = useRef(-1);
  const lastBeatAtRef = useRef(0);
  const lastOnsetAtRef = useRef(0);
  const lastVisualUpdateAtRef = useRef(0);
  const estimatedBpmRef = useRef(MIC_BPM);
  const beatAnchorMsRef = useRef(0);
  const beatIntervalsRef = useRef<number[]>([]);
  const smoothedEnergyRef = useRef(0);
  const baselineEnergyRef = useRef(0.02);
  const previousBassRef = useRef(0);
  const sourceNodesRef = useRef<{
    audioContext: AudioContext;
    stream: MediaStream;
    source: MediaStreamAudioSourceNode;
    analyser: AnalyserNode;
    timeData: Uint8Array<ArrayBuffer>;
    freqData: Uint8Array<ArrayBuffer>;
  } | null>(null);

  const {
    setPlayback,
    setCurrentTrack,
    setNextTrack,
    setQueueTracks,
    markTrackPrepared,
    setSpotifyDevice,
    setSyncStatus,
    setActiveEvent,
    setDisplayLyric,
    setLyricsStatus,
    setRunning,
    setMicSignal,
    visualStyle,
    manualBpm,
  } = usePartyStore();

  useEffect(() => {
    if (!enabled || !clientId) return undefined;
    let alive = true;
    setError('');
    setMicSignal(0, 0);
    beatIndexRef.current = -1;
    lastBeatAtRef.current = 0;
    lastOnsetAtRef.current = 0;
    lastVisualUpdateAtRef.current = 0;
    estimatedBpmRef.current = MIC_BPM;
    beatAnchorMsRef.current = 0;
    beatIntervalsRef.current = [];
    smoothedEnergyRef.current = 0;
    baselineEnergyRef.current = 0.02;
    previousBassRef.current = 0;

    async function startMic() {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('Microphone input is not available in this browser.');
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
          },
        });
        if (!alive) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        const audioContext = await getMicAudioContext();
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.74;
        source.connect(analyser);

        sourceNodesRef.current = {
          audioContext,
          stream,
          source,
          analyser,
          timeData: new Uint8Array(new ArrayBuffer(analyser.fftSize)),
          freqData: new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount)),
        };
        setMicSignal(0.04, 0);
      } catch (err) {
        if (!alive) return;
        setError(micErrorMessage(err));
        setSyncStatus('error');
        setRunning(false);
      }
    }

    async function poll() {
      if (!alive) return;
      try {
        setSyncStatus('polling');
        const token = await getValidToken(clientId);
        if (!token) {
          setSyncStatus('stale');
          return;
        }

        const raw = (await getPlaybackState(token)) ?? (await getCurrentlyPlaying(token));
        const snapshot = normalizeSpotifyPlayback(raw);
        if (!snapshot?.trackId) {
          setSyncStatus('stale');
          return;
        }

        clockRef.current.sync(snapshot);
        setSpotifyDevice(snapshot.deviceName || 'Spotify', snapshot.deviceType ? `${snapshot.deviceType} + Mic` : 'Spotify + Mic');
        setSyncStatus('synced');

        const profileKey = manualBpm ? `manual:${manualBpm}` : 'spotify';
        if (snapshot.trackId !== lastTrackIdRef.current || profileKey !== lastProfileKeyRef.current) {
          lastTrackIdRef.current = snapshot.trackId;
          lastProfileKeyRef.current = profileKey;
          beatIndexRef.current = -1;
          setLyricsStatus('loading');

          const audioProfile = manualBpm
            ? makeManualAudioProfile(manualBpm)
            : await getSpotifyAudioProfile(snapshot.trackId, token, true);

          const track = await prepareTrack(
            {
              id: snapshot.trackId,
              title: snapshot.title,
              artist: snapshot.artist,
              album: snapshot.album,
              durationMs: snapshot.durationMs,
              albumArtUrl: snapshot.albumArtUrl,
              audioProfile,
            },
            visualStyle,
            { lazy: false }
          );

          setCurrentTrack(track);
          markTrackPrepared(track.spotifyTrackId);
          setLyricsStatus(track.hasLyrics ? 'synced' : 'missing');

          if (!track.hasLyrics) {
            enrichTrackLyrics(track).then((enriched) => {
              if (!alive || enriched.spotifyTrackId !== lastTrackIdRef.current) return;
              setCurrentTrack(enriched);
              setLyricsStatus(enriched.hasLyrics ? 'synced' : 'missing');
            });
          }
        }

        const queueData = await getQueue(token);
        const queueItems = queueData?.queue as Record<string, unknown>[] | undefined;
        if (queueItems?.length) {
          const previews: QueueTrackPreview[] = queueItems.slice(0, 8).map((item) => {
            const n = normalizeQueueItem(item);
            return {
              id: n.id,
              title: n.title,
              artist: n.artist,
              albumArtUrl: n.albumArtUrl,
              durationMs: n.durationMs,
              prepared: Boolean(getCachedTrack(n.id)),
            };
          });
          setNextTrack(previews[0] ?? null);
          setQueueTracks(previews);
        } else {
          setNextTrack(null);
          setQueueTracks([]);
        }
      } catch {
        setSyncStatus('error');
      }
    }

    startMic();
    poll();
    const pollId = setInterval(poll, POLL_MS);

    const tick = () => {
      rafRef.current = requestAnimationFrame(tick);
      const nodes = sourceNodesRef.current;
      const state = usePartyStore.getState();
      const progressMs = clockRef.current.getProgressMs();
      const isPlaying = clockRef.current.getIsPlaying();
      const hasManualTempo = Boolean(state.manualBpm || state.tapTimes.length >= 2);

      let energy = 0;
      let onset = 0;
      let bass = 0;
      if (nodes) {
        nodes.analyser.getByteTimeDomainData(nodes.timeData);
        nodes.analyser.getByteFrequencyData(nodes.freqData);

        let rms = 0;
        for (let i = 0; i < nodes.timeData.length; i++) {
          const v = (nodes.timeData[i] - 128) / 128;
          rms += v * v;
        }
        rms = Math.sqrt(rms / nodes.timeData.length);

        const bassBins = Math.min(18, nodes.freqData.length);
        for (let i = 0; i < bassBins; i++) bass += nodes.freqData[i] / 255;
        bass /= bassBins;

        energy = Math.min(1, rms * 4.8 + bass * 0.42);
        baselineEnergyRef.current = baselineEnergyRef.current * 0.985 + energy * 0.015;
        const energyOnset = Math.max(0, energy - baselineEnergyRef.current);
        const bassOnset = Math.max(0, bass - previousBassRef.current);
        onset = Math.min(1, energyOnset + bassOnset * 0.75);
        previousBassRef.current = previousBassRef.current * 0.52 + bass * 0.48;
        smoothedEnergyRef.current = smoothedEnergyRef.current * 0.82 + energy * 0.18;
        setMicSignal(quantizeMicValue(smoothedEnergyRef.current), quantizeMicValue(onset));
      }

      const now = performance.now();
      const currentEstimatedBeatMs = 60_000 / estimatedBpmRef.current;
      const onsetThreshold = Math.max(0.055, baselineEnergyRef.current * 0.55);
      const micBeat = !hasManualTempo
        && onset > onsetThreshold
        && smoothedEnergyRef.current > 0.035
        && now - lastBeatAtRef.current > Math.max(170, currentEstimatedBeatMs * 0.38);

      if (micBeat) {
        const interval = lastOnsetAtRef.current ? now - lastOnsetAtRef.current : 0;
        const canonicalInterval = canonicalBeatInterval(interval);
        if (canonicalInterval) {
          beatIntervalsRef.current.push(canonicalInterval);
          if (beatIntervalsRef.current.length > 10) beatIntervalsRef.current.shift();
          const learnedInterval = median(beatIntervalsRef.current);
          if (learnedInterval > 0) {
            const learnedBpm = Math.max(MIN_MIC_BPM, Math.min(MAX_MIC_BPM, Math.round(60_000 / learnedInterval)));
            estimatedBpmRef.current = estimatedBpmRef.current * 0.78 + learnedBpm * 0.22;
          }
        }
        lastOnsetAtRef.current = now;
        lastBeatAtRef.current = now;
        beatAnchorMsRef.current = progressMs;
      }

      const bpm = state.manualBpm
        ?? (state.tapTimes.length >= 2 ? estimateBpmFromTap(state.tapTimes) : Math.round(estimatedBpmRef.current));
      const beatMs = 60_000 / bpm;
      const beatOffsetMs = hasManualTempo ? 0 : beatAnchorMsRef.current % beatMs;
      const metronomeBeat = computeBeatState(
        progressMs,
        { bpm, manualTapTimes: state.tapTimes, beatOffsetMs },
        beatIndexRef.current
      );
      const beat = {
        ...metronomeBeat,
        isBeat: metronomeBeat.isBeat || micBeat,
        beatStrength: Math.max(metronomeBeat.beatStrength, 0.5 + smoothedEnergyRef.current * 2 + onset * 6),
      };
      if (beat.isBeat) beatIndexRef.current = beat.beatIndex;

      const shouldPublishVisualState = now - lastVisualUpdateAtRef.current >= MIC_VISUAL_UPDATE_MS || beat.isBeat;
      if (!shouldPublishVisualState) return;
      lastVisualUpdateAtRef.current = now;

      const track = state.currentTrack;
      let activeEvent = track ? activeVisualEvent(track.visualTimeline, progressMs) : null;
      if (state.sceneOverride && progressMs < state.sceneOverride.endMs) {
        activeEvent = state.sceneOverride;
      } else if (state.sceneOverride && progressMs >= state.sceneOverride.endMs) {
        usePartyStore.setState({ sceneOverride: null });
      }
      if (activeEvent && state.overrides.sceneLocked && state.overrides.forcedSceneId && activeEvent.sceneId !== state.overrides.forcedSceneId) {
        activeEvent = { ...activeEvent, sceneId: state.overrides.forcedSceneId };
      }
      if (activeEvent) {
        const micEnergy = quantizeMicValue(smoothedEnergyRef.current);
        const micOnset = quantizeMicValue(onset);
        activeEvent = {
          ...activeEvent,
          intensity: quantizeMicValue(micIntensityFromEnergy(micEnergy, micOnset)),
          params: {
            ...activeEvent.params,
            micEnergy,
            micOnset,
          },
        };
      }

      const displayLyric = track && state.overrides.lyricsEnabled !== false
        ? lyricForDisplay(
          track.lyrics,
          track.hooks,
          progressMs,
          state.overrides.lyricDisplayMode,
          state.overrides.forceLyricMode
        )
        : '';

      setPlayback(progressMs, isPlaying, beat);
      setActiveEvent(activeEvent);
      setDisplayLyric(displayLyric);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      alive = false;
      clearInterval(pollId);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      const nodes = sourceNodesRef.current;
      if (nodes) {
        nodes.source.disconnect();
        nodes.stream.getTracks().forEach((track) => track.stop());
        nodes.audioContext.close().catch(() => {});
        sourceNodesRef.current = null;
      }
      setMicSignal(0, 0);
    };
  }, [
    enabled,
    clientId,
    visualStyle,
    manualBpm,
    setActiveEvent,
    setCurrentTrack,
    setDisplayLyric,
    setLyricsStatus,
    setMicSignal,
    setNextTrack,
    setPlayback,
    setQueueTracks,
    markTrackPrepared,
    setRunning,
    setSpotifyDevice,
    setSyncStatus,
  ]);

  return { error };
}
